import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WomenProduct } from '../data/womensProducts';
import { wishlistService } from '../services/wishlistService';
import { useAuth } from '../contexts/AuthContext';
import { discountBadgeColor } from '../utils/festivePrice';

interface Props { product: WomenProduct; }

const WomenProductCard: React.FC<Props> = ({ product }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [inWishlist, setInWishlist] = useState(() => user ? wishlistService.isInWishlist(user.id, product.id) : false);

  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    const { added } = wishlistService.toggle(user.id, {
      id: product.id, productId: product.id, name: product.name,
      price: product.price, originalPrice: product.originalPrice,
      imageUrl: product.imageUrl,
      category: product.category, description: product.description,
      detailUrl: `/women/products/${product.id}`,
    });
    setInWishlist(added);
    window.dispatchEvent(new Event('storage'));
  };

  return (
    <div
      className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
      onClick={() => navigate(`/women/products/${product.id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={encodeURI(product.imageUrl)} alt={product.name}
          className={`w-full h-full object-cover transition-transform duration-300 ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute top-2 left-2">
          <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">
            {product.category.charAt(0).toUpperCase() + product.category.slice(1)}
          </span>
        </div>
        {/* Discount Badge */}
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          <span className={`${discountBadgeColor(product.discountPercent)} text-white text-xs font-bold px-2 py-1 rounded shadow-md`}>
            {product.discountPercent}% OFF
          </span>
          <button
            onClick={handleWishlist}
            className="bg-white rounded-full p-1.5 shadow-md hover:scale-110 transition-transform"
            title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <svg className={`w-4 h-4 ${inWishlist ? 'text-red-500 fill-red-500' : 'text-gray-400'}`} fill={inWishlist ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-1 truncate">{product.name}</h3>
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{product.description}</p>
        <div className="mb-2">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-[#E91E63]">₹{product.price}</span>
            <span className="text-sm text-gray-400 line-through">₹{product.originalPrice}</span>
          </div>
          <p className="text-xs text-green-700 font-medium">Save ₹{product.amountSaved}</p>
        </div>
        <p className="text-xs text-gray-500 italic mb-2">Inclusive of all taxes</p>
        {product.availableSizes[0] !== 'One Size' && (
          <p className="text-xs text-gray-500">Sizes: {product.availableSizes.join(', ')}</p>
        )}
      </div>
    </div>
  );
};

export default WomenProductCard;
